import { UserService } from '@core/services/user/UserService';
import { hasAtLeastOneLicenceEnabled } from '@store/modules/data/authentication/helpers';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { Site } from '@superbet-group/web.types';
import { currentSite } from '@src/app/utils/domainHelpers';
import User from '@core/models/user/User';
import { differenceInYears } from '@superbet-group/web.lib.utilities';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import { IState } from '@app/store';
import { IAuthenticationState } from '@store/modules/data/authentication/types';
import { ActionContext } from 'vuex';
import { appLocale } from '@src/config/locale';

export enum VanHeckeLicenceFlow {
    Standard = 'Stanadrd',
    Underage = 'Underage',
    Migration = 'Migration',
}

export class VanHeckeManager {
    private vanHeckeFixedDate = new Date('2024-09-01');

    constructor() {}

    private getVanHeckeDate(): Date {
        return getAccountConfig().vanHeckeDateOverride || this.vanHeckeFixedDate;
    }

    public static isUnderage(user: User): boolean {
        const dob = new Date(user.dateOfBirth);
        const age = differenceInYears(Date.now(), dob);
        return age < 21;
    }

    public getLicenceFlow(user: User): VanHeckeLicenceFlow {
        if (!getAccountConfig().areSitesSplitByLicence) {
            return VanHeckeLicenceFlow.Standard;
        }

        if (VanHeckeManager.isUnderage(user)) {
            return VanHeckeLicenceFlow.Underage;
        }
        if (user.registrationDate < this.getVanHeckeDate()) {
            return VanHeckeLicenceFlow.Migration;
        }

        return VanHeckeLicenceFlow.Standard;
    }
}

export const vanHeckeManager = new VanHeckeManager();

export async function checkLicencePreferences(authContext: ActionContext<IAuthenticationState, IState>) {
    const { dispatch, rootGetters } = authContext;
    const addModalToEnd = (payload?: unknown) => dispatch('ui/modal/addModalToEnd', payload, { root: true });

    const flow = vanHeckeManager.getLicenceFlow(rootGetters['data/authentication/user']);

    if (flow === VanHeckeLicenceFlow.Underage) {
        addModalToEnd(modalCodes.migrationUnderage);
        return;
    }

    const response = await UserService.getInstance().getUserLicencePreferences();
    if (!response.error && response.data?.licences && !hasAtLeastOneLicenceEnabled(response.data.licences)) {
        switch (flow) {
            case VanHeckeLicenceFlow.Migration:
                addModalToEnd(modalCodes.licenceMigration);
                break;
            case VanHeckeLicenceFlow.Standard:
            default:
                addModalToEnd(
                    {
                        code: modalCodes.licencePreferences,
                        isClosable: false,
                        data: response.data,
                    },
                );
        }
    }
}

type NapoleonSite = Extract<Site, 'NapoleonSports' | 'NapoleonGames' | 'NapoleonDice' | 'NapoleonCasino'>;

export const LicenceMap: Record<NapoleonSite, 'A+' | 'B+' | 'F+' | ''> = {
    [Site.NapoleonSports]: 'F+',
    [Site.NapoleonDice]: 'B+',
    [Site.NapoleonCasino]: 'A+',
    [Site.NapoleonGames]: '',
};

export function getCurrentBelgiumLicence(): string {
    return LicenceMap[currentSite as NapoleonSite];
}

export function getHubUrl(): string {
    const extraPathing = window.location.hostname.split('.').slice(0, -2).join('.');
    const subdomain = extraPathing ? `${extraPathing}.` : '';
    const port = window.location.port ? `:${window.location.port}` : '';
    return `https://${subdomain}napoleongames.be${port}/${appLocale.locale.toLowerCase()}/home`;
}
